/* eslint-disable react/prop-types */
/* eslint-disable react/forbid-prop-types */
import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AttachmentIcon from '@mui/icons-material/Attachment';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import { useMutation } from '@apollo/client';

import RoundedButton from '@app/components/atoms/RoundedButton';
import {
  Preview,
  IconContainer,
  UploaderContainer,
  Label,
  Container,
  PreviewContainer,
  LoadingIcon,
} from './style';
import Typography from '../../../atoms/Typography';
import { UPLOAD_FILES } from '../../../../apollo/mutations';
import Spacer from '../../../atoms/Spacer';
import { useTranslation } from '../../../../config/i18n';

const FilesUploader = ({
  id,
  name,
  onChange,
  files,
  icon,
  onClick,
  onIconClick,
  label,
  multiple,
  onFileClick,
  uploaderText,
  disabled,
  type,
  autoUpload,
  value,
}) => {
  const fileInputRef = useRef(null);

  // Upload file:
  const [
    uploadFiles,
    {
      loading: mutationLoading,
      // error: mutationError, data: mutationData
    },
  ] = useMutation(UPLOAD_FILES, {
    onCompleted: (uploadedData) => {
      if (uploadedData) {
        const { uploadFiles: uploadedFiles } = uploadedData;
        onChange(uploadedFiles);
      }
    },
  });

  const { t } = useTranslation('common')

  const [fileCache, setFileCache] = useState([]);

  let accept = null;
  let previewIcon = <AttachmentIcon color="primary" fontSize="1.8rem" />;
  if (type === 'audio') {
    accept = 'audio/*';
    previewIcon = <AudiotrackIcon color="primary" fontSize="1.8rem" />;
  }

  // Recursion function that will read and validate files then it'll cached it
  //  inside (imagePreviewUrl) State
  const readFiles = fileItems => {
    const newFileCache = multiple ? fileCache : [];
    const newCount = newFileCache.length + fileItems.length;

    const readFile = index => {
      if (index >= fileItems.length) return;
      const file = fileItems[index];
      newFileCache.push({ name: file.name, size: file.size });
      if (newFileCache.length === newCount) {
        setFileCache([...newFileCache]);
      }
      readFile(index + 1);
    };

    readFile(0);
  };

  useEffect(() => {
    if (files.length > 0) {
      readFiles(files);
    }
  }, []);

  const handleFilesChange = e => {
    e.preventDefault();
    const fileItems = Array.from(e.target.files);
    // Read and cache the files
    readFiles(fileItems);

    // onChange(fileItems);
    // upload files and return Upload object
    if (autoUpload) {
      uploadFiles({
        variables: {
          files: fileItems,
        },
      });
    } else {
      onChange(fileItems);
    }
  };

  const open = () => {
    fileInputRef.current.click();
  };

  return (
    <Container>
      <Label >{label}</Label>
      <PreviewContainer name={name}>
        {(multiple || (!multiple && fileCache.length === 0 && files.length === 0)) && (
          <RoundedButton
            variant="outlined"
            color='primary'
            sx={{ width: '20%', height: '48px' }}
            onClick={() => {
              fileInputRef.current.click();
              onClick();
            }}
            disabled={disabled}
            startIcon={<AddIcon />}
          >
            {uploaderText}
          </RoundedButton>
          // <UploaderContainer>
          //   <Uploader
          //     onClick={() => {
          //       fileInputRef.current.click();
          //       onClick();
          //     }}
          //     disabled={disabled}
          //   >
          //     <UploadIcon color="primary" />
          //     {uploaderText}
          //   </Uploader>
          // </UploaderContainer>
        )
        }
        {
          (files.length > 0 && multiple) ||
          (!multiple &&
            files.length > 0 &&
            fileCache.length === 0 &&
            files.map((file, index) => {
              const clear = () => {
                const newFileCache = [...fileCache];
                newFileCache.splice(index, 1);
                setFileCache(newFileCache);
              };
              return (
                <UploaderContainer id={`${id}-preview-${index}`}>
                  <IconContainer onClick={() => onIconClick(open, clear, index)}>
                    {icon}
                  </IconContainer>
                  <Preview onClick={() => onFileClick(open, clear, index)}>
                    {previewIcon}
                    <Typography variant="body1" weight="600">{`${label} :  `}</Typography>
                    <Typography variant="body1">{file.name} </Typography>
                  </Preview>
                </UploaderContainer>
              );
            }))
        }

        {/* Preview the files (Blob or File) */}
        {
          fileCache.length > 0 &&
          fileCache.map((file, index) => {
            const clear = () => {
              const newFileCache = [...fileCache];
              newFileCache.splice(index, 1);

              setFileCache(newFileCache);
            };
            const isUploading = value ? mutationLoading && index >= value.length : true;

            return (
              <UploaderContainer id={`${id}-preview-${index}`}>
                <IconContainer onClick={() => onIconClick(open, clear, index)}>
                  {icon}
                </IconContainer>
                <Preview uploading={isUploading} onClick={() => onFileClick(open, clear, index)}>
                  {previewIcon}
                  <Typography variant="body1" weight="600">{`${label} :  `}</Typography>
                  <Typography variant="body1">{file.name} </Typography>
                </Preview>
                {isUploading && (
                  <LoadingIcon>
                    <CircularProgress color="primary" />
                    <Spacer width="1rem" />
                    <Typography variant="body1" color="primary">
                      {t('uploading')} ...
                    </Typography>
                  </LoadingIcon>
                )}
              </UploaderContainer>
            );
          })
        }

        <input
          hidden
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          onChange={handleFilesChange}
          accept={accept}
        />
      </PreviewContainer >
    </Container >
  );
};

FilesUploader.propTypes = {
  onChange: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(PropTypes.string),
  icon: PropTypes.node,
  onIconClick: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  onClick: PropTypes.func,
  onFileClick: PropTypes.func,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  uploaderText: PropTypes.string,
  type: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  autoUpload: PropTypes.bool,
  value: PropTypes.array,
  name: PropTypes.string,

};

FilesUploader.defaultProps = {
  files: [],
  icon: null,
  onClick: () => { },
  onIconClick: () => { },
  onFileClick: () => { },
  label: '',
  multiple: false,
  uploaderText: '',
  disabled: false,
  autoUpload: true,
  value: [],
  name: '',

};

export default FilesUploader;
